import { useEffect, useMemo, useRef, useState } from 'react';
import type { Track, Video, Visibility } from '@blackblack/shared';
import { extractHashtags } from '@blackblack/shared';
import { api, errorMessage, uploadFile } from '../../lib/api';
import { resetInfinite } from '../../lib/query';
import { goBack, navigate, useSearchParams } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { player } from '../../state/player';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Button, IconButton, Switch, cx } from '../../components/ui/primitives';
import { Cover } from '../music/Cover';
import { Recorder } from './Recorder';
import { SoundPicker } from './SoundPicker';
import styles from './create.module.css';

type Step = 'source' | 'record' | 'edit' | 'details' | 'publishing';
const ACCEPT = 'video/mp4,video/quicktime,video/webm';
const MAX_MB = 200;

interface Clip {
  file: File;
  url: string;
  duration: number;
  width: number;
  height: number;
}

/**
 * Create flow: pick or record → sound, mix & cover → caption & privacy → publish.
 * Editing is stored as a non-destructive EditSpec (server renders it), which is
 * where filters, text, stickers, trimming and multi-clip support plug in later.
 */
export function CreatePage() {
  const toast = useToast();
  const { me } = useAuth();
  const params = useSearchParams();
  const [step, setStep] = useState<Step>('source');
  const [clip, setClip] = useState<Clip | null>(null);
  const [track, setTrack] = useState<Track | null>(null);
  const [picking, setPicking] = useState(false);
  const [originalVolume, setOriginalVolume] = useState(1);
  const [trackVolume, setTrackVolume] = useState(0.8);
  const [coverTime, setCoverTime] = useState(0.5);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [allowComments, setAllowComments] = useState(true);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  // Pre-select a sound when arriving from "Dit geluid gebruiken".
  useEffect(() => {
    const id = params.get('track');
    if (id) void api.get<Track>(`/music/tracks/${id}`).then((t) => t.usableInVideos && setTrack(t)).catch(() => {});
    player.pause();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clip && URL.revokeObjectURL(clip.url), [clip]);
  useEffect(() => {
    if (track) setOriginalVolume(0.2);
  }, [track]);

  const loadFile = async (file: File) => {
    if (!file.type.startsWith('video/')) return toast('Kies een videobestand', { tone: 'error' });
    if (file.size > MAX_MB * 1024 * 1024) return toast(`Video is te groot (max ${MAX_MB} MB)`, { tone: 'error' });
    const url = URL.createObjectURL(file);
    const meta = await new Promise<{ duration: number; width: number; height: number }>((resolve, reject) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => resolve({ duration: Number.isFinite(v.duration) ? v.duration : 0, width: v.videoWidth, height: v.videoHeight });
      v.onerror = () => reject(new Error('unreadable'));
      v.src = url;
    }).catch(() => null);
    if (!meta) {
      URL.revokeObjectURL(url);
      return toast('Deze video kan niet worden gelezen in je browser', { tone: 'error' });
    }
    if (meta.duration > 180) toast('Tip: korte video’s (< 60s) presteren het best', { tone: 'info' });
    setClip({ file, url, ...meta });
    setStep('edit');
  };

  const publish = async () => {
    if (!clip) return;
    setStep('publishing');
    try {
      setPhase('Video uploaden');
      const upload = await uploadFile(clip.file, 'video', (f) => setProgress(f * 0.85));
      setPhase('Cover maken');
      const cover = await captureFrame(clip.url, coverTime).catch(() => null);
      const coverUploadId = cover ? (await uploadFile(cover, 'image')).uploadId : undefined;
      setProgress(0.9);
      setPhase('Verwerken');
      let video = await api.post<Video & { status: string }>('/videos', {
        uploadId: upload.uploadId,
        caption,
        trackId: track?.id,
        visibility,
        allowComments,
        coverUploadId,
        coverTimeSec: coverTime,
        durationSec: clip.duration || undefined,
        width: clip.width || undefined,
        height: clip.height || undefined,
        ...(track ? { originalVolume, trackVolume, trackOffsetSec: 0 } : {}),
      });
      // Server-side processing (transcode, thumbnail, audio mix) runs in the background.
      for (let i = 0; i < 90 && video.status === 'processing'; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        video = await api.get(`/videos/${video.id}`);
      }
      setProgress(1);
      if (video.status === 'failed') throw new Error('Verwerken van je video is mislukt');
      resetInfinite('feed:');
      resetInfinite(`user-videos:${me?.id}`);
      toast(video.status === 'under_review' ? 'Geüpload — je video wordt eerst even gecontroleerd' : 'Je video staat online! 🎉', { tone: 'success' });
      navigate(`/u/${me?.username}`, { replace: true });
    } catch (e) {
      toast(e instanceof Error && !('code' in e) ? e.message : errorMessage(e), { tone: 'error' });
      setStep('details');
    }
  };

  if (step === 'record') {
    return (
      <Recorder
        onCancel={() => setStep('source')}
        onDone={(file) => void loadFile(file)}
      />
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <IconButton
          icon={step === 'source' ? 'close' : 'back'}
          label={step === 'source' ? 'Sluiten' : 'Terug'}
          onClick={() => (step === 'source' ? goBack('/') : step === 'edit' ? setStep('source') : step === 'details' ? setStep('edit') : undefined)}
          disabled={step === 'publishing'}
        />
        <h1>{step === 'source' ? 'Nieuwe video' : step === 'edit' ? 'Bewerken' : step === 'details' ? 'Details' : 'Publiceren'}</h1>
        <span style={{ width: 40 }} />
      </header>

      {step === 'source' && (
        <div className={styles.source}>
          <button className={styles.sourceCard} onClick={() => setStep('record')}>
            <span className={styles.sourceIcon}>
              <Icon name="camera" size={30} />
            </span>
            <strong>Opnemen</strong>
            <small>Neem direct op met je camera (max. 60s)</small>
          </button>
          <button className={styles.sourceCard} onClick={() => fileInput.current?.click()}>
            <span className={styles.sourceIcon}>
              <Icon name="upload" size={30} />
            </span>
            <strong>Uploaden</strong>
            <small>MP4, MOV of WebM · max. {MAX_MB} MB</small>
          </button>
          <input ref={fileInput} type="file" accept={ACCEPT} hidden onChange={(e) => e.target.files?.[0] && void loadFile(e.target.files[0])} />
          <SoundChip track={track} onPick={() => setPicking(true)} onClear={() => setTrack(null)} />
        </div>
      )}

      {step === 'edit' && clip && (
        <div className={styles.edit}>
          <PreviewPlayer clip={clip} track={track} originalVolume={originalVolume} trackVolume={trackVolume} />
          <SoundChip track={track} onPick={() => setPicking(true)} onClear={() => setTrack(null)} />
          {track && (
            <div className={styles.mixer}>
              <label>
                <span>Origineel geluid</span>
                <input type="range" min={0} max={1} step={0.05} value={originalVolume} onChange={(e) => setOriginalVolume(Number(e.target.value))} />
              </label>
              <label>
                <span>Muziek</span>
                <input type="range" min={0} max={1} step={0.05} value={trackVolume} onChange={(e) => setTrackVolume(Number(e.target.value))} />
              </label>
            </div>
          )}
          <CoverPicker clip={clip} time={coverTime} onChange={setCoverTime} />
          <p className={styles.soon}>
            <Icon name="sparkle" size={14} /> Filters, tekst, stickers en knippen komen binnenkort.
          </p>
          <Button size="lg" block onClick={() => setStep('details')}>
            Volgende
          </Button>
        </div>
      )}

      {step === 'details' && clip && (
        <div className={styles.details}>
          <div className={styles.captionRow}>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={2200} rows={5} placeholder="Beschrijf je video… gebruik #hashtags en @vermeldingen" aria-label="Caption" />
            <CoverThumb clip={clip} time={coverTime} />
          </div>
          <div className={styles.tagPreview}>
            {extractHashtags(caption).map((t) => (
              <span key={t}>#{t}</span>
            ))}
            <button onClick={() => setCaption((c) => `${c}${c && !c.endsWith(' ') ? ' ' : ''}#`)}>
              <Icon name="hash" size={14} /> Hashtag
            </button>
            <button onClick={() => setCaption((c) => `${c}${c && !c.endsWith(' ') ? ' ' : ''}@`)}>@ Vermelden</button>
          </div>
          <fieldset className={styles.visibility}>
            <legend>Wie kan deze video zien?</legend>
            {(
              [
                ['public', 'Iedereen', 'globe'],
                ['followers', 'Volgers', 'users'],
                ['private', 'Alleen ik', 'lock'],
              ] as const
            ).map(([v, label, icon]) => (
              <button key={v} className={cx(styles.visOption, visibility === v && styles.visActive)} onClick={() => setVisibility(v)} aria-pressed={visibility === v}>
                <Icon name={icon} size={18} />
                {label}
              </button>
            ))}
          </fieldset>
          <Switch checked={allowComments} onChange={setAllowComments} label="Reacties toestaan" />
          <Button size="lg" block icon="upload" onClick={publish}>
            Publiceren
          </Button>
        </div>
      )}

      {step === 'publishing' && (
        <div className={styles.publishing}>
          <div className={styles.ring} style={{ ['--p' as string]: progress }}>
            <span>{Math.round(progress * 100)}%</span>
          </div>
          <p>{phase}…</p>
          <small>Je kunt dit scherm open laten; dit duurt meestal maar even.</small>
        </div>
      )}

      {picking && (
        <SoundPicker
          onClose={() => setPicking(false)}
          onPick={(t) => {
            setTrack(t);
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

function SoundChip({ track, onPick, onClear }: { track: Track | null; onPick: () => void; onClear: () => void }) {
  return (
    <div className={styles.soundChip}>
      <button onClick={onPick} className={styles.soundMain}>
        {track ? <Cover src={track.coverUrl} size={36} radius={8} /> : <span className={styles.soundIcon}><Icon name="note" size={18} /></span>}
        <span>
          <strong>{track ? track.title : 'Geluid toevoegen'}</strong>
          <small>{track ? track.artist.name : 'Kies muziek uit de BlackBlack-bibliotheek'}</small>
        </span>
      </button>
      {track && <IconButton icon="close" label="Geluid verwijderen" size={18} onClick={onClear} />}
    </div>
  );
}

/** Preview with the chosen mix: video's own audio + the track, kept in sync. */
function PreviewPlayer({ clip, track, originalVolume, trackVolume }: { clip: Clip; track: Track | null; originalVolume: number; trackVolume: number }) {
  const v = useRef<HTMLVideoElement>(null);
  const a = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(true);
  const trackStart = track?.clip?.startSec ?? 0;

  useEffect(() => {
    if (v.current) v.current.volume = originalVolume;
    if (a.current) a.current.volume = trackVolume;
  }, [originalVolume, trackVolume]);

  useEffect(() => {
    const vid = v.current;
    const aud = a.current;
    if (!vid) return;
    const sync = () => {
      if (!aud) return;
      aud.currentTime = trackStart + vid.currentTime;
      if (!vid.paused) void aud.play().catch(() => {});
    };
    const onPause = () => aud?.pause();
    vid.addEventListener('play', sync);
    vid.addEventListener('seeked', sync);
    vid.addEventListener('pause', onPause);
    // looping restarts the music too
    const onTime = () => aud && Math.abs(aud.currentTime - trackStart - vid.currentTime) > 0.35 && sync();
    vid.addEventListener('timeupdate', onTime);
    if (playing) void vid.play().catch(() => setPlaying(false));
    return () => {
      vid.removeEventListener('play', sync);
      vid.removeEventListener('seeked', sync);
      vid.removeEventListener('pause', onPause);
      vid.removeEventListener('timeupdate', onTime);
    };
  }, [track, trackStart]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.preview} onClick={() => {
      const vid = v.current;
      if (!vid) return;
      if (vid.paused) {
        void vid.play();
        setPlaying(true);
      } else {
        vid.pause();
        setPlaying(false);
      }
    }}>
      <video ref={v} src={clip.url} loop playsInline className={styles.previewVideo} />
      {track?.streamUrl && <audio ref={a} src={track.streamUrl} preload="auto" />}
      {!playing && (
        <span className={styles.previewPlay}>
          <Icon name="play" size={48} />
        </span>
      )}
    </div>
  );
}

function CoverPicker({ clip, time, onChange }: { clip: Clip; time: number; onChange: (t: number) => void }) {
  const frames = useMemo(() => Array.from({ length: 6 }, (_, i) => (clip.duration * (i + 0.5)) / 6), [clip.duration]);
  return (
    <div className={styles.coverPicker}>
      <span className={styles.label}>Cover kiezen</span>
      <div className={styles.frames}>
        {frames.map((t) => (
          <button key={t} className={cx(styles.frame, Math.abs(t - time) < clip.duration / 12 && styles.frameActive)} onClick={() => onChange(t)} aria-label={`Frame op ${t.toFixed(1)}s`}>
            <video src={`${clip.url}#t=${t}`} preload="metadata" muted playsInline />
          </button>
        ))}
      </div>
      <input type="range" min={0} max={Math.max(0.1, clip.duration - 0.05)} step={0.05} value={time} onChange={(e) => onChange(Number(e.target.value))} aria-label="Cover-moment" />
    </div>
  );
}

function CoverThumb({ clip, time }: { clip: Clip; time: number }) {
  return (
    <div className={styles.coverThumb}>
      <video src={`${clip.url}#t=${time}`} preload="metadata" muted playsInline />
      <span>Cover</span>
    </div>
  );
}

/** Grabs a JPEG frame from the local video at `time` (used as the cover). */
async function captureFrame(url: string, time: number): Promise<Blob> {
  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;
  v.src = url;
  await new Promise<void>((res, rej) => {
    v.onloadeddata = () => res();
    v.onerror = () => rej(new Error('frame'));
  });
  v.currentTime = Math.min(time, Math.max(0, v.duration - 0.05));
  await new Promise<void>((res) => (v.onseeked = () => res()));
  const scale = Math.min(1, 720 / Math.max(v.videoWidth, v.videoHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(v.videoWidth * scale);
  c.height = Math.round(v.videoHeight * scale);
  c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('frame'))), 'image/jpeg', 0.85));
}
